import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Loader2, Check, Crop } from "lucide-react";
import { toast } from "sonner";
import { snapshotNote, cropToBlob, RATIO } from "@/lib/noteSnapshot";
import { SelectionBox, fitBox } from "./SelectionBox";

const BlurToggle = ({ blur, setBlur }) => (
  <div className="inline-flex rounded-full bg-[#1E1E1E] border border-[#27272A] p-0.5" role="radiogroup" aria-label="Preview style">
    {[[false, "Sharp"], [true, "Slightly blurred"]].map(([v, label]) => (
      <button key={label} type="button" role="radio" aria-checked={blur === v} data-testid={`preview-style-${v ? "blur" : "sharp"}`}
        onClick={() => setBlur(v)}
        className={`px-3 py-1.5 text-xs rounded-full transition-colors ${blur === v ? "bg-[#2563EB] text-white" : "text-[#9CA3AF] hover:text-white"}`}>
        {label}
      </button>
    ))}
  </div>
);

export default function PreviewPicker({ initialBlur = false, onCancel, onDone }) {
  const [shot, setShot] = useState(null);
  const [bounds, setBounds] = useState(null);
  const [box, setBox] = useState(null);
  const [blur, setBlur] = useState(initialBlur);
  const [thumb, setThumb] = useState("");
  const [saving, setSaving] = useState(false);
  const imgRef = useRef(null);
  const canvasRef = useRef(null);

  useEffect(() => {
    const el = document.querySelector('[data-testid="chat-message-list"]');
    if (!el) { toast.error("Open the note's chat to choose a preview"); onCancel(); return; }
    snapshotNote(el)
      .then((c) => { canvasRef.current = c; setShot(c.toDataURL("image/jpeg", 0.85)); })
      .catch((e) => { console.error("snapshot failed", e, e?.target?.src || e?.message); toast.error("Couldn't capture your note"); onCancel(); });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onCancel();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);

  const onImgLoad = () => {
    const b = { w: imgRef.current.clientWidth, h: imgRef.current.clientHeight };
    setBounds(b);
    const w = Math.min(b.w * 0.9, b.h * RATIO);
    setBox(fitBox({ x: (b.w - w) / 2, y: 16, w }, b));
  };

  // Live banner preview of the current box.
  useEffect(() => {
    if (!box || !canvasRef.current || !bounds) return;
    const t = setTimeout(() => cropToBlob(canvasRef.current, box, bounds.w).then((b) => {
      setThumb((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(b); });
    }), 120);
    return () => clearTimeout(t);
  }, [box, bounds]);

  const stageDown = (e) => {
    if (!bounds || e.target !== imgRef.current) return;
    const r = imgRef.current.getBoundingClientRect();
    const px = e.clientX - r.left, py = e.clientY - r.top;
    if (e.pointerType !== "mouse") { setBox((b) => fitBox({ ...b, x: px - b.w / 2, y: py - b.h / 2 }, bounds)); return; }
    e.currentTarget.setPointerCapture(e.pointerId);
    const onMove = (ev) => {
      const w = Math.abs(ev.clientX - r.left - px);
      setBox(fitBox({ x: Math.min(px, ev.clientX - r.left), y: py, w }, bounds));
    };
    const stop = () => { window.removeEventListener("pointermove", onMove); window.removeEventListener("pointerup", stop); };
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", stop);
  };

  const confirm = async () => {
    setSaving(true);
    try {
      const blob = await cropToBlob(canvasRef.current, box, bounds.w);
      onDone({ blob, blur });
    } catch {
      toast.error("Couldn't create the preview");
      setSaving(false);
    }
  };

  return createPortal(
    <div data-testid="preview-picker" className="fixed inset-0 z-[300] pointer-events-auto bg-[#0A0A0A] flex flex-col pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)]">
      <div className="flex items-center gap-3 px-4 py-3 border-b border-[#1E1E1E]">
        <button type="button" data-testid="preview-picker-close-btn" aria-label="Close" onClick={onCancel}
          className="h-9 w-9 rounded-full bg-[#1E1E1E] flex items-center justify-center text-[#D1D5DB] hover:text-white"><X className="h-4 w-4" /></button>
        <div className="min-w-0">
          <div className="text-sm font-semibold text-[#F3F4F6] flex items-center gap-1.5"><Crop className="h-4 w-4 text-[#3B82F6]" /> Choose your preview picture</div>
          <div className="text-[11px] text-[#9CA3AF] truncate">Scroll your note, tap where the box should go, then drag or resize it</div>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        {!shot ? (
          <div className="h-full flex flex-col items-center justify-center gap-3 text-sm text-[#9CA3AF]">
            <Loader2 className="h-6 w-6 animate-spin text-[#3B82F6]" /> Taking a snapshot of your note…
          </div>
        ) : (
          <div className="relative mx-auto max-w-3xl select-none" onPointerDown={stageDown}>
            <img ref={imgRef} src={shot} alt="Snapshot of your note" onLoad={onImgLoad} draggable={false}
              data-testid="preview-snapshot" className="block w-full h-auto" />
            {box && bounds && <SelectionBox box={box} setBox={setBox} bounds={bounds} />}
          </div>
        )}
      </div>

      <div className="border-t border-[#1E1E1E] px-4 py-3 space-y-3 bg-[#121212]">
        <div className="flex items-center gap-3">
          <div className="h-16 rounded-lg overflow-hidden bg-[#1E1E1E] shrink-0 border border-[#27272A]" style={{ width: 64 * RATIO }}>
            {thumb && <img data-testid="preview-live-thumb" src={thumb} alt="Banner preview" className={`h-full w-full object-cover ${blur ? "blur-[3px] scale-110" : ""}`} />}
          </div>
          <div className="min-w-0 space-y-1.5">
            <div className="text-[11px] text-[#9CA3AF]">How buyers see it</div>
            <BlurToggle blur={blur} setBlur={setBlur} />
          </div>
        </div>
        <div className="flex gap-2">
          <button type="button" data-testid="preview-picker-cancel-btn" onClick={onCancel}
            className="flex-1 h-10 rounded-full border border-[#27272A] text-sm text-[#9CA3AF] hover:text-white">Cancel</button>
          <button type="button" data-testid="preview-picker-confirm-btn" onClick={confirm} disabled={!box || saving}
            className="flex-1 h-10 rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] text-sm font-medium text-white flex items-center justify-center gap-1.5 disabled:opacity-40">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Use this preview
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
